import { useMemo } from 'react';
import { Tag, Typography, Button, Tooltip, message } from 'antd';
import {
  SmartDataTableProps,
  SmartTableColumn,
} from '@src/components/SmartDataTable/types';

import {
  useScheduledDateRender,
  useUserAvatarRender,
  useUserTimestampRender,
} from '@src/hooks';
import {
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
  OUTCOME_FILTER_OPTIONS,
} from '../constants';
import { useStatusRender } from './useStatusRender';
import { useTypeRender } from './useTypeRender';
import { useOutcomeRender } from './useOutcomeRender';
import {
  EyeOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { FaWhatsapp } from 'react-icons/fa';
import dayjs from 'dayjs';
import { MultilineTextField } from '@src/components/shared/fields';
import { generateWhatsAppTemplate, generateWhatsAppUrl } from '../utils/whatsappTemplates';
import { apiClient } from '@src/utils/axios';

const { Text } = Typography;

interface UseFollowUpsColumnsOptions {
  showCreatedAt?: boolean;
}

export const useFollowUpsColumns = (
  options: UseFollowUpsColumnsOptions = {}
): SmartTableColumn[] => {
  const { showCreatedAt = true } = options;
  const getStatusConfig = useStatusRender();
  const getTypeConfig = useTypeRender();
  const getOutcomeConfig = useOutcomeRender();
  const renderFollowUpDate = useScheduledDateRender();
  const renderUserAvatar = useUserAvatarRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  // Handle patient info click to redirect to patient details
  const handlePatientInfoClick = (record: any, e: any) => {
    e.stopPropagation();
    // Only redirect if patient_id exists and is not 0
    if (record.patient_id && record.patient_id !== 0) {
      const url = `${window.location.origin}/patients/${record.patient_id}`;
      window.open(url, '_blank');
    }
  };

  // Helper function to get resource name from follow-up type (same as useReferenceDetails)
  const getResource = (type: string) => {
    switch (type) {
      case 'APPOINTMENT':
        return 'appointments';
      case 'PATIENT':
        return 'patients';
      case 'LEAD':
        return 'leads';
      case 'FEEDBACK':
        return 'treatments';
      default:
        return null;
    }
  };

  // Handle WhatsApp click with dynamic template
  const handleWhatsAppClick = async (record: any, e: any) => {
    e.stopPropagation();
    
    if (!record.patient_phone) {
      message.warning('Patient phone number is not available');
      return;
    }

    // Start with available data from record
    let templateData: any = {
      type: record.type,
      patient_name: record.patient_name,
      patient_phone: record.patient_phone,
      follow_up_date: record.follow_up_date,
      remark: record.remark,
      scheduled_datetime: record.scheduled_datetime,
      service_type: record.service_type,
      treatment_date: record.treatment_date,
      treatment_name: record.treatment_name,
      interest: record.interest,
      source: record.source,
    };

    // If we have reference_id and type, fetch additional details using the same pattern as useReferenceDetails
    if (record.reference_id && record.type) {
      const resource = getResource(record.type);
      
      if (resource) {
        try {
          // Use apiClient to fetch reference details (same as dataProvider)
          const { data } = await apiClient.get(`/${resource}/${record.reference_id}`);
          const details = data?.data || data;

          if (details) {
            // Enhance template data with fetched details based on type
            if (record.type === 'APPOINTMENT') {
              templateData.scheduled_datetime = details.scheduled_datetime || templateData.scheduled_datetime;
              templateData.service_type = details.service_type || templateData.service_type;
              // Also get patient name and phone from appointment if available
              if (details.patient_name && !templateData.patient_name) {
                templateData.patient_name = details.patient_name;
              }
              if (details.patient_mobile_no && !templateData.patient_phone) {
                templateData.patient_phone = details.patient_mobile_no;
              }
            } else if (record.type === 'FEEDBACK') {
              templateData.treatment_date = details.treatment_date || details.date || templateData.treatment_date;
              templateData.treatment_name = details.treatment_name || details.name || templateData.treatment_name;
              // Also get patient info from treatment if available
              if (details.patient) {
                const patientName = `${details.patient.first_name || ''} ${details.patient.last_name || ''}`.trim();
                if (patientName && !templateData.patient_name) {
                  templateData.patient_name = patientName;
                }
                if (details.patient.mobile_no && !templateData.patient_phone) {
                  templateData.patient_phone = details.patient.mobile_no;
                }
              }
            } else if (record.type === 'LEAD') {
              templateData.interest = details.interest || templateData.interest;
              templateData.source = details.source || templateData.source;
              // Also get patient name and phone from lead if available
              if (details.patient_name && !templateData.patient_name) {
                templateData.patient_name = details.patient_name;
              }
              if (details.patient_mobile_no && !templateData.patient_phone) {
                templateData.patient_phone = details.patient_mobile_no;
              }
            } else if (record.type === 'PATIENT') {
              // For patient type, get patient details
              const patientName = `${details.first_name || ''} ${details.last_name || ''}`.trim();
              if (patientName && !templateData.patient_name) {
                templateData.patient_name = patientName;
              }
              if (details.mobile_no && !templateData.patient_phone) {
                templateData.patient_phone = details.mobile_no;
              }
            }
          }
        } catch (error) {
          // If fetch fails, continue with available data from record
          console.warn('Failed to fetch reference details:', error);
          // Don't show error to user, just use available data
        }
      }
    }

    console.log('templateData', templateData);

    // Generate template and open WhatsApp
    const template = generateWhatsAppTemplate(templateData);
    const url = generateWhatsAppUrl(record.patient_phone, template);
    window.open(url, '_blank');
  };

  return useMemo(() => {
    const baseColumns: SmartTableColumn[] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'type',
        title: 'Type',
        dataIndex: 'type',
        sorter: false,
        minWidth: 140,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...TYPE_FILTER_OPTIONS],
        render: (type: string) => getTypeConfig(type),
      },
      {
        key: 'reference_id',
        title: 'Reference ID',
        dataIndex: 'reference_id',
        sorter: false,
        minWidth: 120,
        render: (referenceId: any) => {
          if (!referenceId) return null;
          return (
            <Text
              style={{
                fontSize: '12px',
                color: '#1890ff',
                backgroundColor: '#e6f7ff',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: 500,
              }}
            >
              #{referenceId}
            </Text>
          );
        },
      },
      {
        key: 'patient',
        title: 'Patient Info',
        dataIndex: 'patient_name',
        sorter: false,
        minWidth: 200,
        render: (patientName: any, record: any) => {
          const isClickable = record.patient_id && record.patient_id !== 0;

          return (
            <div
              onClick={e => {
                handlePatientInfoClick(record, e);
              }}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                padding: '2px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }
              }}
              onMouseLeave={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
              title={isClickable ? 'Click to view patient details' : undefined}
            >
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {patientName || ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#8c8c8c',
                    fontWeight: 500,
                  }}
                >
                  {record.patient_phone ? '+' + record.patient_phone : ''}
                </div>
                {record.patient_phone && (
                  <Tooltip title="Send WhatsApp message with template">
                    <FaWhatsapp
                      style={{ fontSize: '18px', color: '#25D366', cursor: 'pointer' }}
                      onClick={e => handleWhatsAppClick(record, e)}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: 'follow_up_date',
        title: 'Follow Up Date',
        dataIndex: 'follow_up_date',
        sorter: true,
        minWidth: 180,
        filterable: true,
        filterType: 'daterange',
        render: (followUpDate: any, record: any) =>
          renderFollowUpDate(followUpDate, record.status),
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: false,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: string) => {
          const config = getStatusConfig(status);
          return (
            <Tag
              icon={config.icon}
              style={{
                fontWeight: 500,
                backgroundColor: config.bgColor,
                color: config.textColor,
                borderColor: config.borderColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'outcome',
        title: 'Outcome',
        dataIndex: 'outcome',
        sorter: false,
        minWidth: 140,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...OUTCOME_FILTER_OPTIONS],
        render: (outcome: string) => getOutcomeConfig(outcome),
      },
      {
        key: 'attempt_count',
        title: 'Attempts',
        dataIndex: 'attempt_count',
        sorter: false,
        minWidth: 100,
        render: (attemptCount: number) => (
          <Tag color={attemptCount > 0 ? 'blue' : 'default'}>
            {attemptCount || 0}
          </Tag>
        ),
      },
      {
        key: 'remark',
        title: 'Remark',
        dataIndex: 'remark',
        sorter: false,
        minWidth: 200,
        ellipsis: true,
        render: (remark: string) => <MultilineTextField text={remark} />,
      },
      {
        key: 'created_at',
        title: 'User & Timestamp',
        dataIndex: 'created_at',
        sorter: false,
        minWidth: 180,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at_filter',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    getStatusConfig,
    getTypeConfig,
    getOutcomeConfig,
    renderFollowUpDate,
    renderUserAvatar,
    userTimestampRender,
    showCreatedAt,
  ]);
};
